import { useTranslation } from 'react-i18next';
import { UserRound } from 'lucide-react';

import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useStaffName, useStaffSupport } from './staff-support.store';

const UNASSIGNED = '__none__';

/** A teammate as an initial in a circle, named on hover. */
export function StaffAvatar({ email, className }: { email?: string | null; className?: string }) {
  const nameOf = useStaffName();
  if (!email) return null;
  const name = nameOf(email);
  return (
    <span
      className={cn('flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium uppercase text-foreground', className)}
      title={name}
      aria-label={name}
      data-testid="staff-avatar"
    >
      {name.charAt(0)}
    </span>
  );
}

/** Who handles it: a teammate, or nobody yet ('' — which the API stores to clear it). */
export function AssigneePicker({
  id,
  value,
  onChange,
  size = 'default',
  className,
  me,
}: {
  id?: string;
  value?: string | null;
  onChange: (email: string) => void;
  size?: 'sm' | 'default';
  className?: string;
  /** Marks "(you)" beside the current user. */
  me?: string;
}) {
  const { t } = useTranslation();
  const staff = useStaffSupport((s) => s.staff);
  // Someone assigned before they were on the team list still shows.
  const options = value && !staff.some((m) => m.email === value) ? [...staff, { email: value, name: value.split('@')[0] }] : staff;

  return (
    <Select value={value || UNASSIGNED} onValueChange={(v) => onChange(v === UNASSIGNED ? '' : v)}>
      <SelectTrigger id={id} size={size} className={className} aria-label={t('SUPPORT.STAFF.ASSIGNEE')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNASSIGNED}>
          <UserRound className="size-3.5 text-muted-foreground" />
          {t('SUPPORT.STAFF.UNASSIGNED')}
        </SelectItem>
        <SelectSeparator />
        {options.map((m) => (
          <SelectItem key={m.email} value={m.email}>
            <StaffAvatar email={m.email} className="size-4 text-[9px]" />
            {m.name}
            {m.email === me && <span className="text-muted-foreground">{t('SUPPORT.STAFF.YOU')}</span>}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Filter by who handles it: everyone, me, nobody, or a teammate. Empty string = everyone. */
export function AssigneeFilter({ value, onChange, me, className }: { value: string; onChange: (v: string) => void; me?: string; className?: string }) {
  const { t } = useTranslation();
  const staff = useStaffSupport((s) => s.staff);
  return (
    <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
      <SelectTrigger size="sm" className={className} aria-label={t('SUPPORT.STAFF.FILTER_ASSIGNEE')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{t('SUPPORT.STAFF.EVERYONE')}</SelectItem>
        {me && <SelectItem value={me}>{t('SUPPORT.STAFF.MINE')}</SelectItem>}
        <SelectItem value="none">{t('SUPPORT.STAFF.UNASSIGNED')}</SelectItem>
        <SelectSeparator />
        {staff.filter((m) => m.email !== me).map((m) => (
          <SelectItem key={m.email} value={m.email}>{m.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
